import type { AppConfigOverrides, AppCustomization, ChildAppConfig } from '@shared/app-config'
import type { ProxyConfig } from '@shared/wippy-package/wippy-package'
import type { DevOverlayHandle } from './dev-overlay'
import { mergeChildCustomization } from '@shared/app-config/migration'
import { WIPPY_PACKAGE_DATA_ROLE } from '@wippy-fe/shared'
import {
  getDefaultConfig,
  getDefaultProxyConfig,
  getStoredConfig,
  getStoredProxyConfig,
  isAutoAccept,
  saveConfig,
  saveProxyConfig,
} from './config-store'
import { createDevOverlay } from './dev-overlay'

export interface DevConfigResult {
  appConfig: ChildAppConfig
  proxyConfig: ProxyConfig
  overlay: DevOverlayHandle
}

interface WippyPackageInline {
  wippy?: {
    proxy?: Partial<ProxyConfig>
    configOverrides?: AppConfigOverrides
  }
}

/**
 * Read the consuming app's `package.json` from the inline
 * `<script type="application/json" data-role="@wippy/package">` tag
 * injected by `@wippy-fe/vite-plugin`'s `wippyPackagePlugin()`. Returns
 * `null` if the tag is missing or the JSON fails to parse — apps without
 * the plugin keep working with `getDefaultProxyConfig()` defaults.
 */
function readPackageScript(): WippyPackageInline | null {
  const el = document.querySelector<HTMLScriptElement>(`script[data-role="${WIPPY_PACKAGE_DATA_ROLE}"]`)
  if (!el || !el.textContent)
    return null
  try {
    return JSON.parse(el.textContent) as WippyPackageInline
  }
  catch (err) {
    console.warn(`[wippy-dev] Failed to parse <script data-role="${WIPPY_PACKAGE_DATA_ROLE}">:`, err)
    return null
  }
}

function mergeProxyConfig(base: ProxyConfig, override: Partial<ProxyConfig> | null | undefined): ProxyConfig {
  if (!override)
    return base
  return {
    ...base,
    ...override,
    injections: override.injections
      ? {
          ...base.injections,
          ...override.injections,
          css: {
            ...base.injections.css,
            ...override.injections.css,
          },
        }
      : base.injections,
  }
}

/**
 * Apply per-page `configOverrides.customization` from the consuming
 * app's package.json on top of a base `ChildAppConfig`'s `theming.global`.
 * Delegates to the shared `mergeChildCustomization` helper so dev preview
 * stays in lock-step with the host's `buildChildPayload` merge.
 */
function applyPackageCustomization(
  config: ChildAppConfig,
  customization: AppCustomization | undefined,
): ChildAppConfig {
  if (!customization)
    return config
  const global = mergeChildCustomization({ ...(config.theming?.global ?? {}) }, customization)
  return { ...config, theming: { ...config.theming, global } }
}

function noopRefresh() {
  // eslint-disable-next-line no-console
  console.log('[wippy-dev] Refresh triggered (emitter not yet connected)')
}

export function resolveDevConfig(): Promise<DevConfigResult> {
  const storedConfig = getStoredConfig()
  const storedProxy = getStoredProxyConfig()
  const defaultConfig = getDefaultConfig()
  const defaultProxy = getDefaultProxyConfig()

  // Cascade: defaults → consuming-app package.json (build-time, injected
  // by `@wippy-fe/vite-plugin`'s `wippyPackagePlugin()`) → user-stored
  // overlay tweaks (runtime, highest priority since the user just
  // accepted them in the overlay UI).
  //
  // The package layer applies BOTH on top of `defaultConfig` AND on top
  // of `storedConfig` — otherwise a returning developer who clicked
  // Accept once would be locked out of subsequent `package.json` edits
  // (the stale stored snapshot would mask any new theming added by the
  // app author until the user manually clears localStorage).
  const inline = readPackageScript()
  const packageCustomization = inline?.wippy?.configOverrides?.customization
  const initialProxy = mergeProxyConfig(
    mergeProxyConfig(defaultProxy, inline?.wippy?.proxy ?? null),
    storedProxy,
  )
  const initialConfig = applyPackageCustomization(
    storedConfig ?? defaultConfig,
    packageCustomization,
  )

  // Auto-accept with stored config → resolve immediately, still show overlay for monitoring
  if (isAutoAccept() && storedConfig) {
    // eslint-disable-next-line no-console
    console.log('[wippy-dev] Auto-accepting stored config')

    let resolvedOverlay: DevOverlayHandle | null = null

    const showOverlay = () => {
      resolvedOverlay = createDevOverlay({
        onAccept: (config, proxyConfig) => {
          saveConfig(config)
          saveProxyConfig(proxyConfig)
          // eslint-disable-next-line no-console
          console.log('[wippy-dev] Config updated (reload to apply)')
        },
        onRefresh: noopRefresh,
        initialConfig,
        initialProxyConfig: initialProxy,
        autoAccept: true,
      })
    }

    if (document.body)
      showOverlay()
    else
      addEventListener('DOMContentLoaded', showOverlay)

    // Lazy overlay that forwards calls once the real one exists
    const lazyOverlay: DevOverlayHandle = {
      get element() { return resolvedOverlay?.element || document.createElement('div') },
      destroy: () => resolvedOverlay?.destroy(),
      updatePath: p => resolvedOverlay?.updatePath(p),
      updateTitle: t => resolvedOverlay?.updateTitle(t),
      updateSize: (w, h) => resolvedOverlay?.updateSize(w, h),
      setOnRefresh: (fn) => {
        if (resolvedOverlay)
          resolvedOverlay.setOnRefresh(fn)
        // If overlay not yet created, the DOMContentLoaded handler will use the default
      },
      notifyWaiting: () => {},
    }

    return Promise.resolve({
      appConfig: initialConfig,
      proxyConfig: initialProxy,
      overlay: lazyOverlay,
    })
  }

  // Show overlay and wait for user to accept
  return new Promise((resolve) => {
    const showOverlay = () => {
      const overlay = createDevOverlay({
        onAccept: (config, proxyConfig) => {
          saveConfig(config)
          saveProxyConfig(proxyConfig)
          resolve({ appConfig: config, proxyConfig, overlay })
        },
        onRefresh: noopRefresh,
        initialConfig,
        initialProxyConfig: initialProxy,
        autoAccept: isAutoAccept(),
      })
      overlay.notifyWaiting()
    }

    if (document.body)
      showOverlay()
    else
      addEventListener('DOMContentLoaded', showOverlay)
  })
}
